'use client'

import { useEffect, useRef } from 'react'

/**
 * "Living Thermal" Hintergrund-Canvas.
 * - Animierter Gradient-Mesh (langsam rotierende Warm/Kalt-Zonen)
 * - Aufsteigende Partikel (Thermikblasen)
 * - Reagiert subtil auf Scroll + Mausposition
 * Performant: single rAF, 2D-Context, device-pixel-ratio kappt bei 2.
 */
function withAlpha(hex: string, a: number): string {
  const s = (hex || '').trim()
  if (s.startsWith('#') && (s.length === 7 || s.length === 4)) {
    const full = s.length === 4
      ? '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3]
      : s
    const r = parseInt(full.substring(1, 3), 16)
    const g = parseInt(full.substring(3, 5), 16)
    const b = parseInt(full.substring(5, 7), 16)
    return `rgba(${r},${g},${b},${a})`
  }
  // Fallback: wenn Theme-Var nicht als #hex vorliegt (z.B. color-mix), nimm raw
  return s || `rgba(0,0,0,${a})`
}

export function LivingThermal({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    let raf = 0
    let w = 0
    let h = 0
    let dpr = Math.min(2, window.devicePixelRatio || 1)
    let t = 0
    let mouseX = 0.5
    let mouseY = 0.5
    let targetMX = 0.5
    let targetMY = 0.5
    let scrollY = 0

    // Theme-abhaengige Farben — bei Theme-Wechsel per MutationObserver neu gelesen
    const colors = {
      sky: '#aac7dd',
      ink: '#f5f1e8',
      gold: '#8a5300',
      petrolHi: '#2a6e82',
      cirrus3: '#6b7b8f',
    }
    function readColors() {
      const cs = getComputedStyle(document.documentElement)
      const g = (name: string, fallback: string) =>
        (cs.getPropertyValue(name).trim() || fallback)
      colors.sky      = g('--award-sky',       '#aac7dd')
      colors.ink      = g('--bg-primary',      '#f5f1e8')
      colors.gold     = g('--award-gold',      '#8a5300')
      colors.petrolHi = g('--award-petrol-hi', '#2a6e82')
      colors.cirrus3  = g('--award-cirrus-3',  '#6b7b8f')
    }

    type Bubble = {
      x: number; y: number; r: number; vy: number; alpha: number; hue: number
    }
    const bubbles: Bubble[] = []

    function resize() {
      dpr = Math.min(2, window.devicePixelRatio || 1)
      const rect = canvas!.getBoundingClientRect()
      w = rect.width
      h = rect.height
      canvas!.width = Math.floor(w * dpr)
      canvas!.height = Math.floor(h * dpr)
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    function seed() {
      bubbles.length = 0
      const n = Math.max(28, Math.min(64, Math.floor((w * h) / 22000)))
      for (let i = 0; i < n; i++) {
        bubbles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 0.5 + Math.random() * 2.8,
          vy: 0.15 + Math.random() * 0.4,
          alpha: 0.08 + Math.random() * 0.25,
          hue: Math.random(),
        })
      }
    }

    function onMouse(e: MouseEvent) {
      targetMX = e.clientX / window.innerWidth
      targetMY = e.clientY / window.innerHeight
    }
    function onScroll() {
      scrollY = window.scrollY || 0
    }

    function frame() {
      t += reduce ? 0.0010 : 0.0028
      mouseX += (targetMX - mouseX) * 0.03
      mouseY += (targetMY - mouseY) * 0.03

      ctx!.clearRect(0, 0, w, h)

      // Himmel-Gradient aus Theme-Farben (oben --award-sky, unten --bg-primary)
      const sky = ctx!.createLinearGradient(0, 0, 0, h)
      sky.addColorStop(0,    withAlpha(colors.sky, 0.45))
      sky.addColorStop(0.55, withAlpha(colors.ink, 0.25))
      sky.addColorStop(1,    withAlpha(colors.ink, 0.0))
      ctx!.fillStyle = sky
      ctx!.fillRect(0, 0, w, h)

      // Warme Sonnenzone — theme-akzent (--award-gold)
      const cx1 = w * (0.72 + Math.sin(t * 0.28) * 0.06 + (mouseX - 0.5) * 0.03)
      const cy1 = h * (0.22 + Math.cos(t * 0.22) * 0.05 + (mouseY - 0.5) * 0.02) - scrollY * 0.08
      const g1 = ctx!.createRadialGradient(cx1, cy1, 10, cx1, cy1, Math.max(w, h) * 0.6)
      g1.addColorStop(0,   withAlpha(colors.gold, 0.38))
      g1.addColorStop(0.4, withAlpha(colors.gold, 0.12))
      g1.addColorStop(1,   withAlpha(colors.gold, 0))
      ctx!.fillStyle = g1
      ctx!.fillRect(0, 0, w, h)

      // Horizontlinie — zarter Petrol aus Theme
      ctx!.strokeStyle = withAlpha(colors.petrolHi, 0.16)
      ctx!.lineWidth = 1
      ctx!.beginPath()
      const horizon = h * 0.74 - scrollY * 0.015
      ctx!.moveTo(0, horizon)
      for (let x = 0; x <= w; x += 16) {
        ctx!.lineTo(x, horizon + Math.sin((x + t * 30) * 0.005) * 1.8)
      }
      ctx!.stroke()

      // Cumulus-Partikel — warm=Gold, cool=Cirrus-3 aus Theme
      for (const b of bubbles) {
        b.y -= b.vy * 0.6
        b.x += Math.sin((b.y + t * 20) * 0.005) * 0.2
        if (b.y < -10) {
          b.y = h + 10
          b.x = Math.random() * w
        }
        const warm = b.hue > 0.5
        ctx!.beginPath()
        ctx!.arc(b.x, b.y, b.r, 0, Math.PI * 2)
        ctx!.fillStyle = warm
          ? withAlpha(colors.gold,    b.alpha * 0.45)
          : withAlpha(colors.cirrus3, b.alpha * 0.35)
        ctx!.fill()
      }

      raf = requestAnimationFrame(frame)
    }

    resize()
    seed()
    readColors()
    frame()

    // Theme-Wechsel auf <html data-theme="..."> -> Farben neu lesen
    const themeObs = new MutationObserver(readColors)
    themeObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    })

    window.addEventListener('resize', resize)
    window.addEventListener('mousemove', onMouse, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      cancelAnimationFrame(raf)
      themeObs.disconnect()
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', onMouse)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      className={`award-hero-canvas ${className}`}
      aria-hidden="true"
    />
  )
}
